import { useMemo } from 'react';
import { useAppConfig } from '../contexts/AppConfigContext';
import { permissionManager } from '../business/auth/PermissionManager';
import type { UserRoleType } from '../business/users/UserRoleConfig';

export interface Permissions {
  roleType: UserRoleType;
  /** True when the current user has the Super User role (highest tenant-side privilege). */
  isSuperUser: boolean;
  canManageAllBookings: boolean;
  canViewBookings: boolean;
  canViewOwnBookingsOnly: boolean;
  canManageRequests: boolean;
  canReassignUsers: boolean;
  canRescheduleAppointments: boolean;
  canCancelAppointments: boolean;
  canChangeStatuses: boolean;
  canCreateOwnStop: boolean;
  canDeleteOwnAdHocStop: boolean;
  canBeAssignedToEvents: boolean;
  canViewBookingForms: boolean;
  canEditBookingForms: boolean;
  canCreateBookingForms: boolean;
  canDeleteBookingForms: boolean;
  canEditCompanySettings: boolean;
  canViewAllDashboard: boolean;
  isOwnDashboardOnly: boolean;
  hasBookingManagementAccess: boolean;
  canViewBilling: boolean;
  /** Super Users and Scheduling Assistants can access the Voice Agent area. */
  canAccessVoiceAgent: boolean;
  hasPermission: (permission: string) => boolean;
}

export function usePermissions(): Permissions {
  const { config } = useAppConfig();

  return useMemo(() => {
    const roleType = permissionManager.determineUserRoleType(config);
    return ({
    roleType,
    isSuperUser: roleType === 'super_user',
    canManageAllBookings: permissionManager.canManageAllBookings(config),
    canViewBookings: permissionManager.canViewBookings(config),
    canViewOwnBookingsOnly: permissionManager.canViewOwnBookingsOnly(config),
    canManageRequests: permissionManager.canManageRequests(config),
    canReassignUsers: permissionManager.canReassignUsers(config),
    canRescheduleAppointments: permissionManager.canRescheduleAppointments(config),
    canCancelAppointments: permissionManager.canCancelAppointments(config),
    canChangeStatuses: permissionManager.canChangeStatuses(config),
    canCreateOwnStop: permissionManager.canCreateOwnStop(config),
    canDeleteOwnAdHocStop: permissionManager.canDeleteOwnAdHocStop(config),
    canBeAssignedToEvents: permissionManager.canBeAssignedToEvents(config),
    canViewBookingForms: permissionManager.canViewBookingForms(config),
    canEditBookingForms: permissionManager.canEditBookingForms(config),
    canCreateBookingForms: permissionManager.canCreateBookingForms(config),
    canDeleteBookingForms: permissionManager.canDeleteBookingForms(config),
    canEditCompanySettings: permissionManager.canEditCompanySettings(config),
    canViewAllDashboard: permissionManager.canViewAllDashboard(config),
    isOwnDashboardOnly: permissionManager.isOwnDashboardOnly(config),
    hasBookingManagementAccess: permissionManager.hasBookingManagementAccess(config),
    canViewBilling: permissionManager.canViewBilling(config),
    canAccessVoiceAgent: permissionManager.canAccessVoiceAgent(config),
    hasPermission: (permission: string) => permissionManager.hasPermission(config, permission),
    });
  }, [config]);
}
